<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div>
    <oxd-text v-if="error" tag="p" class="anna-prompts-error">{{
      error
    }}</oxd-text>
    <div
      v-for="p in shown"
      :key="p.key"
      class="orangehrm-card-container anna-prompts"
    >
      <div class="anna-prompts-head">
        <oxd-text tag="h6" class="orangehrm-main-title">{{ p.title }}</oxd-text>
        <span v-if="!p.isDefault" class="anna-prompts-badge">edited</span>
        <oxd-button
          v-if="collapsible"
          class="anna-prompts-toggle"
          display-type="text"
          :icon-name="isOpen(p) ? 'chevron-up' : 'chevron-down'"
          :label="isOpen(p) ? 'Hide' : 'Edit'"
          :disabled="dirty(p)"
          @click="open[p.key] = !open[p.key]"
        />
      </div>
      <oxd-text tag="p" class="anna-prompts-purpose">{{ p.purpose }}</oxd-text>
      <oxd-text tag="p" class="anna-prompts-meta">
        {{
          p.savedAt
            ? `Last saved ${p.savedAt.slice(0, 16)} UTC${
                p.savedBy ? ` by ${p.savedBy}` : ''
              }`
            : 'The default text.'
        }}
        <template v-if="p.isDefault"> · the default</template>
      </oxd-text>
      <div v-show="isOpen(p)">
        <oxd-text v-if="hasMarks(p)" tag="p" class="anna-prompts-marks">
          <span v-pre
            >Words in curly brackets, like {{ company }} or {fullName}, are
            filled in automatically: keep them exactly as they are. Text between
            ** and ** is only emphasis for ANNA.</span
          >
        </oxd-text>
        <oxd-divider />

        <div
          v-for="(part, i) in drafts[p.key].parts"
          :key="i"
          class="anna-prompts-part"
        >
          <template v-if="part.heading !== null">
            <oxd-text tag="p" class="anna-prompts-part-title">{{
              part.title
            }}</oxd-text>
            <oxd-text v-if="part.hint" tag="p" class="anna-prompts-meta">{{
              part.hint
            }}</oxd-text>
          </template>
          <textarea
            v-model="part.body"
            :class="[
              'anna-prompts-text',
              {'anna-prompts-text--whole': part.heading === null},
            ]"
            :aria-label="part.title || p.title"
            spellcheck="true"
          ></textarea>
        </div>

        <div :class="['anna-prompts-actions', {'anna-save-float': dirty(p)}]">
          <oxd-input-field
            v-if="p.history.length && !dirty(p)"
            v-model="drafts[p.key].version"
            type="select"
            label="Earlier versions"
            :options="versionOptions(p)"
            class="anna-prompts-versions"
          />
          <oxd-text v-if="dirty(p)" tag="p" class="anna-prompts-unsaved"
            >Not saved yet</oxd-text
          >
          <oxd-button
            v-if="dirty(p)"
            display-type="ghost"
            label="Undo my changes"
            :disabled="busy"
            @click="undo(p)"
          />
          <oxd-button
            display-type="ghost"
            label="Reset to default"
            :disabled="busy || p.isDefault"
            @click="reset(p)"
          />
          <oxd-button
            display-type="secondary"
            :label="`Save: ${p.title}`"
            :disabled="busy || !dirty(p)"
            @click="save(p)"
          />
        </div>
      </div>
    </div>
    <oxd-text v-if="!loaded && !error" tag="p" class="anna-prompts-meta"
      >Loading…</oxd-text
    >
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {splitPrompt, joinPrompt} from '../util/promptParts';
import {guardLeaving} from '../util/leaveGuard';

// ANNA Settings shows one card per prompt in several places: they share one load
let loading = null;

// ANNA's prompts for one tab of ANNA Settings, each its own card, saved on its own.
// A long prompt is edited part by part (util/promptParts); every saved version is kept on ANNA.
export default {
  name: 'PromptLibrary',
  props: {
    // which prompts this tab edits, in this order
    keys: {
      type: Array,
      required: true,
    },
    // folded to its title until "Edit" (a card with unsaved changes stays open)
    collapsible: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/anna/prompts',
    );
    return {http};
  },
  data() {
    return {
      prompts: [],
      // key => {parts, version}
      drafts: {},
      open: {},
      loaded: false,
      busy: false,
      error: null,
    };
  },
  computed: {
    shown() {
      return this.keys
        .map((key) => this.prompts.find((p) => p.key === key))
        .filter((p) => p && this.drafts[p.key]);
    },
  },
  watch: {
    // an earlier version goes into the boxes; Save makes it the live one again
    drafts: {
      deep: true,
      handler(drafts) {
        Object.entries(drafts).forEach(([key, draft]) => {
          const p = this.prompts.find((x) => x.key === key);
          const v = draft.version && p?.history[draft.version.id];
          if (v) {
            draft.parts = splitPrompt(v.text);
            draft.version = null;
          }
        });
      },
    },
  },
  created() {
    this.load();
  },
  mounted() {
    this.unguard = guardLeaving(() => this.shown.some((p) => this.dirty(p)));
  },
  beforeUnmount() {
    this.unguard();
  },
  methods: {
    apply(prompts) {
      this.prompts = prompts || [];
      const drafts = {};
      this.prompts.forEach((p) => {
        drafts[p.key] = {parts: splitPrompt(p.text), version: null};
      });
      this.drafts = drafts;
      this.loaded = true;
    },
    load() {
      loading = loading || this.http.request({method: 'GET'});
      loading
        .then(({data: {data}}) => this.apply(data))
        .catch(() => {
          loading = null;
          this.error =
            "ANNA's instructions couldn't be loaded right now (ANNA unavailable?).";
        });
    },
    isOpen(p) {
      return !this.collapsible || !!this.open[p.key] || this.dirty(p);
    },
    dirty(p) {
      return (
        joinPrompt(this.drafts[p.key].parts) !== joinPrompt(splitPrompt(p.text))
      );
    },
    // placeholders or **emphasis** in it: worth a line on what they are
    hasMarks(p) {
      return /\{|\*\*/.test(p.text);
    },
    versionOptions(p) {
      return p.history.map((v, i) => ({
        id: i,
        label: `${(v.savedAt || '').slice(0, 16)}${
          v.savedBy ? ` · ${v.savedBy}` : ''
        }${v.note ? ` · ${v.note}` : ''}`,
      }));
    },
    undo(p) {
      this.drafts[p.key].parts = splitPrompt(p.text);
    },
    send(p, data) {
      this.busy = true;
      return this.http
        .request({method: 'PUT', url: `/api/v2/anna/prompts/${p.key}`, data})
        .then(({data: {data}}) => {
          // other cards keep their unsaved edits
          const kept = {...this.drafts};
          this.apply(data);
          Object.keys(kept).forEach((key) => {
            if (key !== p.key) this.drafts[key] = kept[key];
          });
          return this.$toast.saveSuccess();
        })
        .catch(() => null)
        .finally(() => {
          this.busy = false;
        });
    },
    save(p) {
      const text = joinPrompt(this.drafts[p.key].parts);
      if (!text.trim()) {
        this.$toast.warn({
          title: 'Not saved',
          message: 'It can\'t be empty. Use "Reset to default" instead.',
        });
        return;
      }
      this.send(p, {text});
    },
    reset(p) {
      if (
        window.confirm(
          `Go back to the default "${p.title}"? The current text stays under "Earlier versions".`,
        )
      ) {
        this.send(p, {reset: true});
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-prompts-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.anna-prompts-toggle {
  margin-left: auto;
}
.anna-prompts-purpose {
  font-size: 13px;
  line-height: 1.6;
  color: #3d4757;
  margin-top: 4px;
}
.anna-prompts-meta {
  font-size: 12px;
  color: #64728c;
}
.anna-prompts-error {
  color: #a05a00;
  font-size: 13px;
}
.anna-prompts-marks {
  font-size: 12px;
  line-height: 1.5;
  color: #3d4757;
  background: #f4f7fb;
  border-radius: 8px;
  padding: 6px 10px;
  margin-top: 8px;
}
.anna-prompts-unsaved {
  font-size: 12px;
  font-weight: 700;
  color: #a05a00;
}
.anna-prompts-badge {
  font-size: 11px;
  font-weight: 700;
  color: #a05a00;
  background: #fff3d6;
  border-radius: 999px;
  padding: 1px 8px;
}
.anna-prompts-part {
  margin-bottom: 14px;
}
.anna-prompts-part-title {
  font-size: 14px;
  font-weight: 700;
  color: #16202b;
}
.anna-prompts-text {
  width: 100%;
  box-sizing: border-box;
  min-height: 120px;
  field-sizing: content;
  max-height: 520px;
  margin-top: 6px;
  resize: vertical;
  padding: 10px 12px;
  border: 1px solid #dde3ea;
  border-radius: 10px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.6;
  color: #16202b;
  background: #ffffff;
  &--whole {
    min-height: 260px;
  }
}
.anna-prompts-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 10px;
}
.anna-prompts-versions {
  min-width: 260px;
  margin-right: auto;
}
</style>
